import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/helpers";

const AlarmPage = () => {
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [triggered, setTriggered] = useState([]);
  const [rules, setRules] = useState([]);

  // Load profiles on mount
  useEffect(() => {
    const loadProfiles = async () => {
      try {
        const res = await axios.get(`${API}/admin/users`);
        setProfiles(res.data.users || []);
      } catch {}
    };
    loadProfiles();
  }, []);

  // Load alarms when profile is selected
  useEffect(() => {
    if (!profile) return;
    loadTriggered();
    loadRules();
    const int1 = setInterval(loadTriggered, 3000);
    const int2 = setInterval(loadRules, 10000);
    return () => { clearInterval(int1); clearInterval(int2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const loadTriggered = async () => {
    try {
      const res = await axios.get(`${API}/alarm/triggered?profile=${profile}`);
      setTriggered(res.data.triggered || []);
    } catch {}
  };

  const loadRules = async () => {
    try {
      const res = await axios.get(`${API}/alarm/rules?profile=${profile}`);
      setRules((res.data.rules || []).filter(r => r.active));
    } catch {}
  };

  const dismissAll = async () => {
    try {
      await axios.post(`${API}/alarm/dismiss-all`);
      setTriggered([]);
    } catch {}
  };

  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/alarm/dismiss`, { id });
      setTriggered(prev => prev.filter(t => t.id !== id));
    } catch {}
  };

  const formatPrice = (val) => {
    if (!val && val !== 0) return "0";
    return Number(val).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const profileName = profiles.find(p => p.profile === profile)?.name || profile;

  // ─── PROFILE SELECTION SCREEN ───
  if (!profile) {
    return (
      <div style={{
        minHeight: "100vh", background: "#09301E",
        display: "flex", justifyContent: "center", alignItems: "center", padding: "20px"
      }}>
        <div data-testid="alarm-profile-select" style={{
          background: "#fff", borderRadius: "16px", padding: "40px",
          width: "100%", maxWidth: "400px", textAlign: "center"
        }}>
          <h1 style={{ color: "#09301E", fontSize: "24px", fontWeight: "800", marginBottom: "8px" }}>
            QEN Alarm Paneli
          </h1>
          <p style={{ color: "#888", fontSize: "14px", marginBottom: "28px" }}>Sube Secin</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {profiles.map(p => (
              <button
                key={p.profile}
                onClick={() => setProfile(p.profile)}
                data-testid={`alarm-select-profile-${p.profile}`}
                style={{
                  padding: "18px", borderRadius: "10px", fontSize: "16px", fontWeight: "700",
                  background: "#09301E", color: "#fff", border: "none", cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={e => e.target.style.background = "#0d4a2e"}
                onMouseLeave={e => e.target.style.background = "#09301E"}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── FULL SCREEN ALARM OVERLAY ───
  if (triggered.length > 0) {
    return (
      <div data-testid="alarm-fullscreen" style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
        background: "linear-gradient(135deg, #0a0a0a 0%, #1a0a0a 50%, #0a0a0a 100%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        overflow: "auto", padding: "40px 20px"
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          border: "4px solid #DA9F54",
          animation: "alarmPulse 1.5s ease-in-out infinite",
          pointerEvents: "none"
        }} />

        <style>{`
          @keyframes alarmPulse {
            0%, 100% { border-color: #DA9F54; box-shadow: inset 0 0 60px rgba(218,159,84,0.1); }
            50% { border-color: #ef4444; box-shadow: inset 0 0 100px rgba(239,68,68,0.15); }
          }
          @keyframes alarmBell {
            0%, 100% { transform: rotate(0deg); }
            10%, 30%, 50% { transform: rotate(8deg); }
            20%, 40% { transform: rotate(-8deg); }
            60% { transform: rotate(0deg); }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <div style={{ fontSize: "72px", marginBottom: "20px", animation: "alarmBell 2s ease-in-out infinite" }}>
          <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#DA9F54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </div>

        <h1 style={{
          color: "#DA9F54", fontSize: "clamp(32px, 5vw, 56px)",
          fontWeight: "900", marginBottom: "6px", textAlign: "center",
          textTransform: "uppercase", letterSpacing: "4px"
        }}>FIYAT ALARMI</h1>
        <p style={{ color: "#aaa", fontSize: "14px", marginBottom: "8px" }}>{profileName}</p>
        <p style={{ color: "#888", fontSize: "14px", marginBottom: "40px" }}>{triggered.length} alarm tetiklendi</p>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", maxWidth: "700px" }}>
          {triggered.map((alarm, i) => (
            <div key={alarm.id} data-testid={`triggered-alarm-${alarm.id}`} style={{
              background: "linear-gradient(135deg, #1a1a1a 0%, #222 100%)",
              border: "2px solid #DA9F54", borderRadius: "16px", padding: "24px 30px",
              animation: `fadeInUp 0.5s ease-out ${i * 0.1}s both`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                <div>
                  <div style={{ color: "#DA9F54", fontSize: "clamp(20px, 3vw, 28px)", fontWeight: "800", marginBottom: "6px" }}>{alarm.product_label}</div>
                  <div style={{ color: "#aaa", fontSize: "14px" }}>
                    {alarm.condition === "above" ? "Ustune cikti" : "Altina dustu"}: Hedef {formatPrice(alarm.target)}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: alarm.condition === "above" ? "#22c55e" : "#ef4444", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "900" }}>
                    {formatPrice(alarm.triggered_price)}
                  </div>
                  <button onClick={() => dismiss(alarm.id)} data-testid={`dismiss-${alarm.id}`} style={{
                    marginTop: "8px", padding: "6px 16px", borderRadius: "8px",
                    background: "transparent", border: "1px solid #555", color: "#888", cursor: "pointer", fontSize: "12px"
                  }}>Kapat</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={dismissAll} data-testid="dismiss-all-btn" style={{
          marginTop: "40px", padding: "16px 48px", borderRadius: "12px",
          background: "#DA9F54", color: "#000", border: "none",
          fontSize: "18px", fontWeight: "800", cursor: "pointer",
          letterSpacing: "2px", textTransform: "uppercase"
        }}>TUMUNU KAPAT</button>
      </div>
    );
  }

  // ─── ACTIVE ALARMS LIST ───
  return (
    <div data-testid="alarm-page" style={{
      minHeight: "100vh", background: "linear-gradient(135deg, #0a0a0a 0%, #111 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
        <h1 style={{ color: "#DA9F54", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: "800", letterSpacing: "2px" }}>FIYAT ALARMLARI</h1>
      </div>
      <p style={{ color: "#888", fontSize: "14px", marginBottom: "6px" }}>{profileName}</p>
      <button onClick={() => setProfile(null)} data-testid="alarm-change-profile" style={{
        background: "transparent", border: "1px solid #444", color: "#888", padding: "6px 16px",
        borderRadius: "8px", cursor: "pointer", fontSize: "12px", marginBottom: "30px"
      }}>Sube Degistir</button>

      {rules.length === 0 ? (
        <div style={{
          color: "#444", fontSize: "18px", padding: "60px",
          border: "2px dashed #333", borderRadius: "16px", textAlign: "center"
        }}>Aktif alarm yok. Admin panelinden alarm ekleyebilirsiniz.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", maxWidth: "600px" }}>
          {rules.map(rule => (
            <div key={rule.id} data-testid={`alarm-rule-${rule.id}`} style={{
              background: "#1a1a1a", border: "1px solid #333", borderRadius: "12px",
              padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <div style={{ color: "#DA9F54", fontSize: "16px", fontWeight: "700" }}>{rule.product_label}</div>
                <div style={{ color: "#888", fontSize: "13px", marginTop: "4px" }}>
                  {rule.condition === "above" ? "Ustune cikarsa" : "Altina duserse"}
                </div>
              </div>
              <div style={{ color: "#fff", fontSize: "22px", fontWeight: "800" }}>{formatPrice(rule.target)}</div>
            </div>
          ))}
        </div>
      )}

      <a href="/admin" style={{
        marginTop: "40px", color: "#DA9F54", fontSize: "14px", textDecoration: "none",
        border: "1px solid #DA9F54", padding: "10px 24px", borderRadius: "8px"
      }}>Admin Paneline Git</a>
    </div>
  );
};

export default AlarmPage;
